import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";
import { Dialog, DialogTitle } from "@headlessui/react";
import { useOwners } from "hooks/useOwners.js";
import { useFraccs } from "hooks/useFraccs.js";
import { useAmenities } from "hooks/useAmenities.js";
import { ROUTE_PATHS } from "utils/routes";
import Pagination from "components/misc/Pagination";

const Container = tw.div`relative`;
const Table = tw.table`min-w-full bg-white rounded-lg shadow-md`;
const Th = tw.th`py-3 px-4 bg-primary-500 text-white font-semibold text-left`;
const Td = tw.td`py-2 px-4 border-b border-gray-200 text-gray-700`;
const Tr = tw.tr`hover:bg-gray-100 transition-colors`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight`;
const Button = tw.button`ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition`;
// Mock Fraccionamiento Data and State
const initialFraccForm = { name: "", contact: "", representative: "", mantenimiento: "", amenities: [] };


const AdminOwnersPage = () => {

  // Owners state
  const { owners, fetchOwners, createOwner, updateOwner, deleteOwner } = useOwners();
  // amenities from useAmenities will be used only in modal below
  const { amenities, fetchAmenities } = useAmenities();
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const initialForm = { name: "", email: "", phone: "", houseNumber: "", suburb: "" };
  const [form, setForm] = useState(initialForm);
  const [selectedOwner, setSelectedOwner] = useState(null);
  
  // Individual loading states for better UX
  const [processingOwner, setProcessingOwner] = useState(null);

  // Use Fraccs hook
  // Use Fraccs hook
  // Use Fraccs hook
  const { fraccs, addFracc, deleteFracc, updateFracc } = useFraccs();
  // Fracc modal state
  const [fraccModalOpen, setFraccModalOpen] = useState(false);
  const [fraccEditMode, setFraccEditMode] = useState(false);
  const [fraccForm, setFraccForm] = useState(initialFraccForm);
  const [selectedFracc, setSelectedFracc] = useState(null);
  // Fracc search/filter
  const [fraccSearch, setFraccSearch] = useState("");
  const filteredFraccs = fraccs.filter(f =>
    f.name.toLowerCase().includes(fraccSearch.toLowerCase()) ||
    f.contact.toLowerCase().includes(fraccSearch.toLowerCase()) ||
    f.representative.toLowerCase().includes(fraccSearch.toLowerCase())
  );

  // Owners search/filter
  const [ownerSearch, setOwnerSearch] = useState("");
  const filteredOwners = owners.filter(owner =>
    owner.name.toLowerCase().includes(ownerSearch.toLowerCase()) ||
    owner.email.toLowerCase().includes(ownerSearch.toLowerCase()) ||
    owner.phone.toLowerCase().includes(ownerSearch.toLowerCase()) ||
    owner.houseNumber.toLowerCase().includes(ownerSearch.toLowerCase()) ||
    owner.suburb.toLowerCase().includes(ownerSearch.toLowerCase())
  );

  // Pagination state for owners table (must be after filteredOwners is defined)
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const total = filteredOwners.length;
  const totalPages = Math.ceil(total / pageSize);
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageData = filteredOwners.slice(startIdx, endIdx);
  // Fraccionamiento CRUD handlers
  const openCreateFraccModal = () => {
    setFraccForm(initialFraccForm);
    setFraccEditMode(false);
    setFraccModalOpen(true);
    setSelectedFracc(null);
  };
  const openEditFraccModal = (fracc) => {
    setFraccForm({
      name: fracc.name || "",
      contact: fracc.contact || "",
      representative: fracc.representative || "",
      mantenimiento: fracc.mantenimiento || "",
      amenities: fracc.amenities || []
    });
    setFraccEditMode(true);
    setFraccModalOpen(true);
    setSelectedFracc(fracc);
  };
  const closeFraccModal = () => {
    setFraccModalOpen(false);
    setTimeout(() => {
      setFraccForm(initialFraccForm);
      setSelectedFracc(null);
    }, 200);
  };
  const handleFraccChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === "amenities") {
      let newAmenities = [...(fraccForm.amenities || [])];
      if (checked) {
        newAmenities.push(value);
      } else {
        newAmenities = newAmenities.filter(a => a !== value);
      }
      setFraccForm({ ...fraccForm, amenities: newAmenities });
    } else {
      setFraccForm({ ...fraccForm, [name]: value });
    }
  };
  const handleFraccSubmit = (e) => {
    e.preventDefault();
    if (fraccEditMode) {
      updateFracc({ ...selectedFracc, ...fraccForm });
    } else {
      const newId = fraccs.length > 0 ? Math.max(...fraccs.map(f => f.id)) + 1 : 1;
      addFracc({ ...fraccForm, id: newId });
    }
    closeFraccModal();
  };
  
  const handleFraccDelete = (id) => {
    if (window.confirm("¿Seguro que deseas eliminar este fraccionamiento?")) {
      deleteFracc(id);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchOwners();
      await fetchAmenities();
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  const openCreateModal = () => {
    setForm(initialForm);
    setEditMode(false);
    setModalOpen(true);
    setSelectedOwner(null);
  };
  const openEditModal = (owner) => {
    setForm({
      name: owner.name || "",
      email: owner.email || "",
      phone: owner.phone || "",
      houseNumber: owner.houseNumber || "",
      suburb: owner.suburb || ""
    });
    setEditMode(true);
    setModalOpen(true);
    setSelectedOwner(owner);
  };
  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setForm(initialForm);
      setSelectedOwner(null);
    }, 200); // Wait for modal close animation
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessingOwner('submit');
    
    try {
      if (editMode) {
        await updateOwner({ ...selectedOwner, ...form });
      } else {
        await createOwner(form);
      }
      setForm(initialForm);
      setSelectedOwner(null);
      closeModal();
    } catch (error) {
      console.error('Error processing owner:', error);
    } finally {
      setProcessingOwner(null);
    }
  };

  const handleDelete = async (ownerId) => {
    if (window.confirm("¿Seguro que deseas eliminar este propietario?")) {
      setProcessingOwner(`delete-${ownerId}`);
      try {
        await deleteOwner(ownerId);
      } catch (error) {
        console.error('Error deleting owner:', error);
      } finally {
        setProcessingOwner(null);
      }
    }
  };

  return (
    <AnimationRevealPage>
      <Header />
      <Container className="py-12">
        <Heading>Administración de Cotos</Heading>
        <div className="flex flex-wrap gap-4 my-6 justify-center">
          <input
            type="text"
            placeholder="Buscar coto..."
            value={fraccSearch}
            onChange={e => setFraccSearch(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm"
          />
          <Button onClick={openCreateFraccModal}>Agregar Coto</Button>
        </div>
        <div className="overflow-x-auto mb-12 bg-white/90 backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl">
          <Table>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Contacto</Th>
                <Th>Representante</Th>
                <Th>Amenidades</Th>
                <Th>Mantenimiento</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {filteredFraccs.length === 0 ? (
                <tr><Td colSpan={8}>No hay cotos registrados.</Td></tr>
              ) : (
                filteredFraccs.map(fracc => (
                  <Tr key={fracc.id}>
                    <Td>{fracc.name}</Td>
                    <Td>{fracc.contact}</Td>
                    <Td>{fracc.representative}</Td>
                    <Td>{fracc.preserves}</Td>
                    <Td>${Number(fracc.mantenimiento || 0).toLocaleString()}</Td>
                    <Td>
                      <Button onClick={() => openEditFraccModal(fracc)}>Editar</Button>
                      <Button onClick={() => handleFraccDelete(fracc.id)} className="bg-red-500 hover:bg-red-700 ml-2">Eliminar</Button>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Owners Section */}
        <Heading>Panel de Propietarios</Heading>
        <div className="flex flex-wrap gap-4 my-6 justify-center">
          <input
            type="text"
            placeholder="Buscar propietario..."
            value={ownerSearch}
            onChange={e => setOwnerSearch(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm"
          />
          <Button onClick={openCreateModal}>Agregar Propietario</Button>
        </div>
        <div className="overflow-x-auto bg-white/90 backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl">
          <Table>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Email</Th>
                <Th>Teléfono</Th>
                <Th>Casa</Th>
                <Th>Coto</Th>
                <Th>Moroso</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                if (owners.length === 0) {
                  return <tr><Td colSpan={7}>No hay propietarios encontrados.</Td></tr>;
                } else if (filteredOwners.length === 0) {
                  return <tr><Td colSpan={7}>No hay propietarios que coincidan con la búsqueda.</Td></tr>;
                } else {
                  return [
                    ...pageData.map(owner => (
                      <Tr key={owner.ownerId}>
                        <Td>
                          <a
                            className="text-blue-600 cursor-pointer border-b-2 border-blue-500 hover:text-blue-800 font-semibold transition px-1"
                            href={`${ROUTE_PATHS.ADMINPAYMENTPAGE}/${owner.ownerId}`}
                          >
                            {owner.name}
                          </a>
                        </Td>
                        <Td>{owner.email}</Td>
                        <Td>{owner.phone}</Td>
                        <Td>{owner.houseNumber}</Td>
                        <Td>{owner.suburb}</Td>
                        <Td>{owner.moroso ? 'Sí' : 'No'}</Td>
                        <Td>
                          <Button onClick={() => openEditModal(owner)}>Editar</Button>
                          <Button 
                            onClick={() => handleDelete(owner.ownerId)} 
                            className="bg-red-500 hover:bg-red-700 ml-2"
                            disabled={processingOwner === `delete-${owner.ownerId}`}
                          >
                            {processingOwner === `delete-${owner.ownerId}` ? 'Eliminando...' : 'Eliminar'}
                          </Button>
                        </Td>
                      </Tr>
                    )),
                    <tr key="pagination-controls">
                      <Td colSpan={7}>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      </Td>
                    </tr>
                  ];
                }
              })()}
            </tbody>
          </Table>
        </div>
        {/* Fraccionamiento Modal */}
        {fraccModalOpen && (
          <Dialog open={fraccModalOpen} onClose={closeFraccModal} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">{fraccEditMode ? "Editar Coto" : "Agregar Coto"}</DialogTitle>
              <form onSubmit={handleFraccSubmit}>
                <div className="mb-4">
                  <label htmlFor="fracc-name" className="block mb-1 font-semibold">Nombre</label>
                  <input id="fracc-name" name="name" value={fraccForm.name} onChange={handleFraccChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="fracc-contact" className="block mb-1 font-semibold">Contacto</label>
                  <input id="fracc-contact" name="contact" value={fraccForm.contact} onChange={handleFraccChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="fracc-representative" className="block mb-1 font-semibold">Representante</label>
                  <input id="fracc-representative" name="representative" value={fraccForm.representative} onChange={handleFraccChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="fracc-mantenimiento" className="block mb-1 font-semibold">Mantenimiento ($)</label>
                  <input id="fracc-mantenimiento" name="mantenimiento" type="number" min="0" value={fraccForm.mantenimiento} onChange={handleFraccChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-6">
                  <label htmlFor="amenities-group" className="block mb-1 font-semibold">Amenidades</label>
                  <div id="amenities-group" className="flex flex-wrap gap-2">
                    {amenities.map(a => (
                      <label key={a.name} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          name="amenities"
                          value={a.name}
                          checked={fraccForm.amenities?.includes(a.name)}
                          onChange={handleFraccChange}
                        />
                        <span>{a.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" onClick={closeFraccModal} className="bg-gray-400 hover:bg-gray-600">Cancelar</Button>
                  <Button type="submit">{fraccEditMode ? "Guardar Cambios" : "Agregar"}</Button>
                </div>
              </form>
            </div>
          </Dialog>
        )}
        {modalOpen && (
          <Dialog open={modalOpen} onClose={closeModal} as="div" className="fixed inset-0 z-40 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">{editMode ? "Editar Propietario" : "Agregar Propietario"}</DialogTitle>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="owner-name" className="block mb-1 font-semibold">Nombre</label>
                  <input id="owner-name" name="name" value={form.name} onChange={handleChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="owner-email" className="block mb-1 font-semibold">Email</label>
                  <input id="owner-email" name="email" type="email" value={form.email} onChange={handleChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="owner-phone" className="block mb-1 font-semibold">Teléfono</label>
                  <input id="owner-phone" name="phone" value={form.phone} onChange={handleChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="owner-houseNumber" className="block mb-1 font-semibold">Casa</label>
                  <input id="owner-houseNumber" name="houseNumber" value={form.houseNumber} onChange={handleChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-6">
                  <label htmlFor="owner-suburb" className="block mb-1 font-semibold">Fraccionamiento</label>
                  <select
                    id="owner-suburb"
                    name="suburb"
                    value={form.suburb}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="" disabled>Selecciona un fraccionamiento</option>
                    {fraccs.map(fracc => (
                      <option key={fracc.id} value={fracc.name}>{fracc.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" onClick={closeModal} className="bg-gray-400 hover:bg-gray-600">Cancelar</Button>
                  {(() => {
                    let buttonLabel;
                    if (processingOwner === 'submit') {
                      buttonLabel = 'Guardando...';
                    } else if (editMode) {
                      buttonLabel = 'Guardar Cambios';
                    } else {
                      buttonLabel = 'Agregar';
                    }
                    return (
                      <Button 
                        type="submit" 
                        disabled={processingOwner === 'submit'}
                      >
                        {buttonLabel}
                      </Button>
                    );
                  })()}
                </div>
              </form>
            </div>
          </Dialog>
        )}
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AdminOwnersPage;
